import rgba from "polished/lib/color/rgba";

import { flattenSemanticColors } from "./utils";

export type Colors = {
  [key: string]: string;
} & {
  [K in keyof typeof baseColors]: string;
} & {
  [K in keyof typeof jigsawV2Colors]: string;
} & {
  [K in keyof typeof flatSemanticColorsDark]: string;
};

export const baseColors = {
  black: "#000000",
  white: "#FFFFFF",
  neutral: "#121221",

  everWhite: "#FFFFFF",
  everBlack: "#000000",

  // Base colors for the new jigsaw v2 color tokens
  pink500: "#FF63EF",
  orange500: "#FC7413",
};

export const jigsawV2Colors = {
  black25: rgba(baseColors.black, 0.025),
  black50: rgba(baseColors.black, 0.05),
  black100: rgba(baseColors.black, 0.1),
  black200: rgba(baseColors.black, 0.2),
  black300: rgba(baseColors.black, 0.3),
  black400: rgba(baseColors.black, 0.4),
  black500: rgba(baseColors.black, 0.5),
  black600: rgba(baseColors.black, 0.6),
  black700: rgba(baseColors.black, 0.7),
  black800: rgba(baseColors.black, 0.8),
  black900: rgba(baseColors.black, 0.9),

  white25: rgba(baseColors.white, 0.025),
  white50: rgba(baseColors.white, 0.05),
  white100: rgba(baseColors.white, 0.1),
  white200: rgba(baseColors.white, 0.2),
  white300: rgba(baseColors.white, 0.3),
  white400: rgba(baseColors.white, 0.4),
  white500: rgba(baseColors.white, 0.5),
  white600: rgba(baseColors.white, 0.6),
  white700: rgba(baseColors.white, 0.7),
  white800: rgba(baseColors.white, 0.8),
  white900: rgba(baseColors.white, 0.9),

  pink100V2: "#FFE0FC",
  pink200V2: "#FFC1F9",
  pink300V2: "#FFA1F5",
  pink400V2: "#FF82F2",
  pink500V2: "#FF63EF",
  pink600V2: "#CC4FBF",
  pink700V2: "#993B8F",
  pink800V2: "#662860",
  pink900V2: "#331430",

  red100V2: "#FAD7E2",
  red200V2: "#F5AFC4",
  red300V2: "#F186A7",
  red400V2: "#EC5E89",
  red500V2: "#E7366C",
  red600V2: "#B92B56",
  red700V2: "#8B2041",
  red800V2: "#5C162B",
  red900V2: "#2E0B16",

  blue100V2: "#E0F3FF",
  blue200V2: "#C1E8FF",
  blue300V2: "#A1DCFF",
  blue400V2: "#82D1FF",
  blue500V2: "#63C5FF",
  blue600V2: "#4F9ECC",
  blue700V2: "#3B7699",
  blue800V2: "#284F66",
  blue900V2: "#142733",

  purple100V2: "#F2E9FC",
  purple200V2: "#E6D2F8",
  purple300V2: "#D9BCF5",
  purple400V2: "#CDA5F1",
  purple500V2: "#C08FEE",
  purple600V2: "#9A72BE",
  purple700V2: "#73568F",
  purple800V2: "#4D395F",
  purple900V2: "#261D30",

  orange100V2: "#FEE3D0",
  orange200V2: "#FEC7A1",
  orange300V2: "#FDAC71",
  orange400V2: "#FD9042",
  orange500V2: "#FC7413",
  orange600V2: "#CA5D0F",
  orange700V2: "#97460B",
  orange800V2: "#652E08",
  orange900V2: "#321704",

  yellow100V2: "#FDF8DF",
  yellow200V2: "#FCF1BF",
  yellow300V2: "#FAE9A0",
  yellow400V2: "#F9E280",
  yellow500V2: "#F7DB60",
  yellow600V2: "#C6AF4D",
  yellow700V2: "#94833A",
  yellow800V2: "#635826",
  yellow900V2: "#312C13",

  green100V2: "#DCFEEE",
  green200V2: "#B9FDDD",
  green300V2: "#96FCCD",
  green400V2: "#73FBBC",
  green500V2: "#50F9AB",
  green600V2: "#40C889",
  green700V2: "#309667",
  green800V2: "#206444",
  green900V2: "#103222",

  neutral25: "#F9F9FA",
  neutral50: "#F3F3F4",
  neutral100: "#E8E8EA",
  neutral200: "#D1D0D4",
  neutral300: "#B9B9BF",
  neutral400: "#A2A2A9",
  neutral500: "#8B8A94",
  neutral600: "#74737F",
  neutral700: "#5D5C69",
  neutral800: "#2E2D3E",
  neutral900: "#171629",
  neutral: "#121221",
};

export const baseColorsInverted = {
  black: "#FFFFFF",
  white: "#000000",
  neutral: "#121221",

  everWhite: "#FFFFFF",
  everBlack: "#000000",

  pink500: "#FF63EF",
  orange500: "#FC7413",
};

export const jigsawV2ColorsInverted = {
  black25: rgba(baseColorsInverted.black, 0.9),
  black50: rgba(baseColorsInverted.black, 0.8),
  black100: rgba(baseColorsInverted.black, 0.7),
  black200: rgba(baseColorsInverted.black, 0.6),
  black300: rgba(baseColorsInverted.black, 0.5),
  black400: rgba(baseColorsInverted.black, 0.4),
  black500: rgba(baseColorsInverted.black, 0.3),
  black600: rgba(baseColorsInverted.black, 0.2),
  black700: rgba(baseColorsInverted.black, 0.1),
  black800: rgba(baseColorsInverted.black, 0.05),
  black900: rgba(baseColorsInverted.black, 0.025),

  white25: rgba(baseColorsInverted.white, 0.9),
  white50: rgba(baseColorsInverted.white, 0.8),
  white100: rgba(baseColorsInverted.white, 0.7),
  white200: rgba(baseColorsInverted.white, 0.6),
  white300: rgba(baseColorsInverted.white, 0.5),
  white400: rgba(baseColorsInverted.white, 0.4),
  white500: rgba(baseColorsInverted.white, 0.3),
  white600: rgba(baseColorsInverted.white, 0.2),
  white700: rgba(baseColorsInverted.white, 0.1),
  white800: rgba(baseColorsInverted.white, 0.05),
  white900: rgba(baseColorsInverted.white, 0.025),

  pink900V2: "#FFE0FC",
  pink800V2: "#FFC1F9",
  pink700V2: "#FFA1F5",
  pink600V2: "#FF82F2",
  pink500V2: "#FF63EF",
  pink400V2: "#CC4FBF",
  pink300V2: "#993B8F",
  pink200V2: "#662860",
  pink100V2: "#331430",

  red900V2: "#FAD7E2",
  red800V2: "#F5AFC4",
  red700V2: "#F186A7",
  red600V2: "#EC5E89",
  red500V2: "#E7366C",
  red400V2: "#B92B56",
  red300V2: "#8B2041",
  red200V2: "#5C162B",
  red100V2: "#2E0B16",

  blue900V2: "#E0F3FF",
  blue800V2: "#C1E8FF",
  blue700V2: "#A1DCFF",
  blue600V2: "#82D1FF",
  blue500V2: "#63C5FF",
  blue400V2: "#4F9ECC",
  blue300V2: "#3B7699",
  blue200V2: "#284F66",
  blue100V2: "#142733",

  purple900V2: "#F2E9FC",
  purple800V2: "#E6D2F8",
  purple700V2: "#D9BCF5",
  purple600V2: "#CDA5F1",
  purple500V2: "#C08FEE",
  purple400V2: "#9A72BE",
  purple300V2: "#73568F",
  purple200V2: "#4D395F",
  purple100V2: "#261D30",

  orange900V2: "#FEE3D0",
  orange800V2: "#FEC7A1",
  orange700V2: "#FDAC71",
  orange600V2: "#FD9042",
  orange500V2: "#FC7413",
  orange400V2: "#CA5D0F",
  orange300V2: "#97460B",
  orange200V2: "#652E08",
  orange100V2: "#321704",

  yellow900V2: "#FDF8DF",
  yellow800V2: "#FCF1BF",
  yellow700V2: "#FAE9A0",
  yellow600V2: "#F9E280",
  yellow500V2: "#F7DB60",
  yellow400V2: "#C6AF4D",
  yellow300V2: "#94833A",
  yellow200V2: "#635826",
  yellow100V2: "#312C13",

  green900V2: "#DCFEEE",
  green800V2: "#B9FDDD",
  green700V2: "#96FCCD",
  green600V2: "#73FBBC",
  green500V2: "#50F9AB",
  green400V2: "#40C889",
  green300V2: "#309667",
  green200V2: "#206444",
  green100V2: "#103222",

  neutral900: "#F9F9FA",
  neutral800: "#F3F3F4",
  neutral700: "#E8E8EA",
  neutral600: "#D1D0D4",
  neutral500: "#B9B9BF",
  neutral400: "#A2A2A9",
  neutral300: "#8B8A94",
  neutral200: "#74737F",
  neutral100: "#5D5C69",
  neutral50: "#2E2D3E",
  neutral25: "#171629",
  neutral: "#121221",
};

// Semantic color tokens
//
// Naming schema:
// [elementType]-[priority/mood]-[level]
// [elementType] is the type of element that the color is used on: surface, outline, text
// [priority/mood] is the priority or mood of the color: primary, secondary, tertiary, error, positive, warning, special
// [level] is the level of the color: moreSubdue, subdue, base, highlight, moreHighlight, mostHighlight
//
// Example:
// surface-primary-moreSubdue
// text-monetize-subdue
// outline-special-base

// Dark theme colors
const surface = {
  primary: {
    moreSubdue: jigsawV2Colors.green900V2,
    subdue: jigsawV2Colors.green600V2,
    base: jigsawV2Colors.green500V2,
    highlight: jigsawV2Colors.green400V2,
    moreHighlight: jigsawV2Colors.green100V2,
  },

  monetize: {
    moreSubdue: jigsawV2Colors.yellow900V2,
    subdue: jigsawV2Colors.yellow700V2,
    base: jigsawV2Colors.yellow500V2,
    highlight: jigsawV2Colors.yellow400V2,
  },

  // TODO: add something for purple900V2
  special: {
    base: jigsawV2Colors.purple500V2,
    highlight: jigsawV2Colors.purple400V2,
  },

  error: {
    mostSubdue: jigsawV2Colors.red900V2,
    moreSubdue: jigsawV2Colors.red800V2,
    subdue: jigsawV2Colors.red700V2,
    base: jigsawV2Colors.red500V2,
    highlight: jigsawV2Colors.red400V2,
  },

  warning: {
    moreSubdue: jigsawV2Colors.orange900V2,
    subdue: jigsawV2Colors.orange600V2,
    base: jigsawV2Colors.orange500V2,
    highlight: jigsawV2Colors.orange300V2,
    moreHighlight: jigsawV2Colors.orange200V2,
    mostHighlight: jigsawV2Colors.orange100V2,
  },

  info: {
    mostSubdue: jigsawV2Colors.blue900V2,
    subdue: jigsawV2Colors.blue600V2,
    base: jigsawV2Colors.blue500V2,
    highlight: jigsawV2Colors.blue400V2,
    moreHighlight: jigsawV2Colors.blue200V2,
    mostHighlight: jigsawV2Colors.blue100V2,
  },

  transparentInverted: {
    subdue: jigsawV2Colors.black100,
    base: jigsawV2Colors.black200,
    highlight: jigsawV2Colors.black400,
  },

  transparent: {
    moreSubdue: jigsawV2Colors.white25,
    subdue: jigsawV2Colors.white50,
    base: jigsawV2Colors.white100,
    highlight: jigsawV2Colors.white200,
  },

  neutral: {
    subdue: jigsawV2Colors.neutral900,
    base: jigsawV2Colors.neutral800,
    highlight: jigsawV2Colors.neutral700,
    moreHighlight: jigsawV2Colors.neutral600,
    mostHighlight: jigsawV2Colors.neutral400,
  },
};

const outline = {
  special: {
    subdue: jigsawV2Colors.purple400V2,
    base: jigsawV2Colors.purple500V2,
    highlight: jigsawV2Colors.purple600V2,
  },
  error: {
    subdue: jigsawV2Colors.red500V2,
    base: jigsawV2Colors.red500V2,
    highlight: jigsawV2Colors.red600V2,
  },
  positive: {
    subdue: jigsawV2Colors.green400V2,
    base: jigsawV2Colors.green500V2,
    highlight: jigsawV2Colors.green600V2,
  },
  // TODO: missing an info outline
  monetize: {
    subdue: jigsawV2Colors.yellow400V2,
    base: jigsawV2Colors.yellow500V2,
    highlight: jigsawV2Colors.yellow600V2,
  },
  // TODO: missing an inverted neutral outline
  neutral: {
    mostSubdue: jigsawV2Colors.white25,
    moreSubdue: jigsawV2Colors.white50,
    subdue: jigsawV2Colors.white100,
    base: jigsawV2Colors.white200,
    highlight: jigsawV2Colors.white500,
    moreHighlight: jigsawV2Colors.white700,
    mostHighlight: baseColors.white,
  },
};

const text = {
  link: {
    subdue: jigsawV2Colors.blue400V2,
    base: jigsawV2Colors.blue500V2,
    highlight: jigsawV2Colors.blue600V2,
  },
  monetize: {
    subdue: jigsawV2Colors.yellow400V2,
    base: jigsawV2Colors.yellow500V2,
    highlight: jigsawV2Colors.yellow600V2,
  },
  info: {
    subdue: jigsawV2Colors.blue300V2,
    base: jigsawV2Colors.blue400V2,
    highlight: jigsawV2Colors.blue500V2,
  },
  positive: {
    subdue: jigsawV2Colors.green400V2,
    base: jigsawV2Colors.green500V2,
    highlight: jigsawV2Colors.green600V2,
  },
  error: {
    subdue: jigsawV2Colors.red300V2,
    base: jigsawV2Colors.red400V2,
    highlight: jigsawV2Colors.red500V2,
  },
  warning: {
    subdue: jigsawV2Colors.orange400V2,
    base: jigsawV2Colors.orange500V2,
    highlight: jigsawV2Colors.orange600V2,
    moreHighlight: jigsawV2Colors.orange700V2,
  },
  special: {
    subdue: jigsawV2Colors.purple400V2,
    base: jigsawV2Colors.purple500V2,
    highlight: jigsawV2Colors.purple600V2,
  },

  neutral: {
    mostSubdue: jigsawV2Colors.white200,
    moreSubdue: jigsawV2Colors.white300,
    subdue: jigsawV2Colors.white500,
    base: jigsawV2Colors.white700,
    highlight: baseColors.white,
    moreHighlight: jigsawV2Colors.white800,
  },

  inverted: {
    moreSubdue: jigsawV2Colors.black200,
    subdue: jigsawV2Colors.black600,
    base: baseColors.black,
  },
};

export const semanticColorsDark = {
  surface,
  outline,
  text,
};
const flatSemanticColorsDark = flattenSemanticColors(semanticColorsDark);

const darkThemeColors = {
  ...baseColors,
  ...jigsawV2Colors,
  ...flatSemanticColorsDark,
} as Colors;

// Light theme colors
const lightSurface = {
  primary: {
    moreSubdue: jigsawV2Colors.green800V2,
    subdue: jigsawV2Colors.green700V2,
    base: jigsawV2Colors.green600V2,
    highlight: jigsawV2Colors.green500V2,
    moreHighlight: jigsawV2Colors.green400V2,
  },

  monetize: {
    moreSubdue: jigsawV2Colors.yellow200V2,
    subdue: jigsawV2Colors.yellow300V2,
    base: jigsawV2Colors.yellow400V2,
    highlight: jigsawV2Colors.yellow100V2,
  },

  special: {
    base: jigsawV2Colors.purple600V2,
    highlight: jigsawV2Colors.purple500V2,
  },

  error: {
    mostSubdue: jigsawV2Colors.red100V2,
    moreSubdue: jigsawV2Colors.red200V2,
    subdue: jigsawV2Colors.red300V2,
    base: jigsawV2Colors.red500V2,
    highlight: jigsawV2Colors.red100V2,
  },

  warning: {
    moreSubdue: jigsawV2Colors.orange800V2,
    subdue: jigsawV2Colors.orange700V2,
    base: jigsawV2Colors.orange600V2,
    highlight: jigsawV2Colors.orange500V2,
    moreHighlight: jigsawV2Colors.orange400V2,
    mostHighlight: jigsawV2Colors.orange300V2,
  },

  info: {
    mostSubdue: jigsawV2Colors.blue200V2,
    subdue: jigsawV2Colors.blue400V2,
    base: jigsawV2Colors.blue500V2,
    highlight: jigsawV2Colors.blue600V2,
    moreHighlight: jigsawV2Colors.blue700V2,
    mostHighlight: jigsawV2Colors.blue800V2,
  },

  transparentInverted: {
    subdue: jigsawV2Colors.white100,
    base: jigsawV2Colors.white200,
    highlight: jigsawV2Colors.white400,
  },

  transparent: {
    moreSubdue: jigsawV2Colors.black25,
    subdue: jigsawV2Colors.black50,
    base: jigsawV2Colors.black100,
    highlight: jigsawV2Colors.black300,
  },

  neutral: {
    subdue: jigsawV2Colors.neutral25,
    base: baseColors.white,
    highlight: jigsawV2Colors.neutral100,
    moreHighlight: jigsawV2Colors.neutral200,
    mostHighlight: jigsawV2Colors.neutral300,
  },
};

const lightOutline = {
  special: {
    subdue: jigsawV2Colors.purple500V2,
    base: jigsawV2Colors.purple600V2,
    highlight: jigsawV2Colors.purple700V2,
  },

  error: {
    subdue: jigsawV2Colors.red500V2,
    base: jigsawV2Colors.red400V2,
    highlight: jigsawV2Colors.red300V2,
  },

  positive: {
    subdue: jigsawV2Colors.green700V2,
    base: jigsawV2Colors.green600V2,
    highlight: jigsawV2Colors.green500V2,
  },

  monetize: {
    subdue: jigsawV2Colors.yellow600V2,
    base: jigsawV2Colors.yellow500V2,
    highlight: jigsawV2Colors.yellow400V2,
  },

  neutral: {
    mostSubdue: jigsawV2Colors.black50,
    moreSubdue: jigsawV2Colors.black100,
    subdue: jigsawV2Colors.black200,
    base: jigsawV2Colors.black300,
    highlight: jigsawV2Colors.black500,
    moreHighlight: jigsawV2Colors.black700,
    mostHighlight: baseColors.black,
  },
};

const lightText = {
  link: {
    subdue: jigsawV2Colors.blue800V2,
    base: jigsawV2Colors.blue700V2,
    highlight: jigsawV2Colors.blue600V2,
  },

  monetize: {
    subdue: jigsawV2Colors.yellow800V2,
    base: jigsawV2Colors.yellow700V2,
    highlight: jigsawV2Colors.yellow600V2,
  },

  info: {
    subdue: jigsawV2Colors.blue900V2,
    base: jigsawV2Colors.blue800V2,
    highlight: jigsawV2Colors.blue700V2,
  },

  positive: {
    subdue: jigsawV2Colors.green900V2,
    base: jigsawV2Colors.green800V2,
    highlight: jigsawV2Colors.green700V2,
  },

  error: {
    subdue: jigsawV2Colors.red700V2,
    base: jigsawV2Colors.red600V2,
    highlight: jigsawV2Colors.red500V2,
  },

  warning: {
    subdue: jigsawV2Colors.orange700V2,
    base: jigsawV2Colors.orange600V2,
    highlight: jigsawV2Colors.orange500V2,
    moreHighlight: jigsawV2Colors.orange400V2,
  },

  special: {
    subdue: jigsawV2Colors.purple600V2,
    base: jigsawV2Colors.purple500V2,
    highlight: jigsawV2Colors.purple400V2,
  },

  neutral: {
    mostSubdue: jigsawV2Colors.black400,
    moreSubdue: jigsawV2Colors.black500,
    subdue: jigsawV2Colors.black700,
    base: jigsawV2Colors.black900,
    highlight: baseColors.black,
    moreHighlight: jigsawV2Colors.black800,
  },

  inverted: {
    moreSubdue: jigsawV2Colors.white800,
    subdue: jigsawV2Colors.white600,
    base: baseColors.white,
  },
};

export const semanticColorsLight = {
  surface: lightSurface,
  outline: lightOutline,
  text: lightText,
};
const flatSemanticColorsLight = flattenSemanticColors(semanticColorsLight);

const lightThemeColors = {
  ...baseColorsInverted,
  ...jigsawV2ColorsInverted,
  ...flatSemanticColorsLight,
} as Colors;

export const colors = darkThemeColors;
export const darkColors = darkThemeColors;
export const lightColors = lightThemeColors;

export const externalBrandColors = {
  adp: {
    logoFill: "#FFFFFF",
    fillBis: "#CF4037",
  },
  amazon: {
    logoBackground: "#FFFFFF",
  },
  americanExpress: {
    logoBackground: "#016FD0",
    fillColor: "#FFFFFF",
  },
  angelList: {
    logoBackground: "#000000",
    fillColor: "#000000",
  },
  bankOfAmerica: {
    logoBackground: "#000000",
  },
  billDotCom: {
    orangePantone: "#FF5A0A",
    atomicTangerine: "#FF9C6C",
    logoBackground: "#EFEFF0",
  },
  bluevine: {
    logoBackground: "#FFFFFF",
  },
  brex: {
    logoBackground: "#171717",
    fillColor: "#F8F8FA",
  },
  capitalOne: {
    logoBackground: "#FFFFFF",
    logoFill: "#013D5B",
    brandColor: "#CC2427",
  },
  chase: {
    logoBackground: "#0659A5",
    logoBackgroundBrandIcons: "#FFFFFF",
    logoFill: "#FFFFFF",
    altLogoFill: "#126BC5",
  },
  citi: {
    logoBackground: "#FFFFFF",
  },
  captable: {
    logoBackground: "#FD9800",
    fillColor: "#FFFFFF",
  },
  carta: {
    logoBackground: "#1B98ED",
    logoBackgroundBrandIcons: "#E3EEF3",
    logoFill: "#FFFFFF",
  },
  central: {
    logoBackground: "#1A0DAB",
  },
  causal: {
    logoBackground: "#FFFFFF",
    logoFill: "#C3D9F3",
  },
  daydream: {
    logoBackground: "#FFFFFF",
    logoFill: "#FFFFFF",
    brandColor: "#FF50BC",
  },
  deel: {
    logoBackground: "#FFFFFF",
    logoFill: "#FFFFFF",
    brandColor: "#15357A",
  },
  elanCreditCard: {
    logoBackground: "#FFFFFF",
  },
  every: {
    logoBackground: "#161B2C",
    logoFill: "#FFFFFF",
    brandColor: "#3F69FF",
  },
  expensify: {
    logoBackground: "#0B1B34",
    brandColorBlue: "#2EAAE2",
    brandColorGreen: "#2ECB70",
    brandColorOrange: "#FFC600",
    brandColorWhite: "#ffffff",
    brandColorGray: "#313E48",
  },
  firstNationalBankOfOmaha: {
    logoBackground: "#FFFFFF",
  },
  firstRepublic: {
    logoBackground: "#FFFFFF",
    brandColor: "#AD861E",
  },
  google: {
    logoBackground: "#FFFFFF",
    logoFill: "#FFFFFF",
  },
  gusto: {
    logoBackground: "#F45D48",
    backgroundColor: "#FFFFFF",
  },
  joiin: {
    logoBackground: "#1D2400",
    logoFill: "#FFFFFF",
    brandColor: "#00a0df",
  },
  justworks: {
    logoBackground: "#1E98FF",
  },
  ltse: {
    logoBackground: "#110837",
  },
  meow: {
    logoBackground: "#FFFFFF",
    logoBackgroundBrandIcons: "#0C1D37",
    brandColor: "#FF6531",
    roundedBackground: "#F2DFD6",
  },
  mercury: {
    logoBackground: "#5466F9",
    logoFill: "#FFFFFF",
  },
  novo: {
    logoBackground: "#FFFFFF",
  },
  paypal: {
    logoBackground: "#FFFFFF",
  },
  pnc: {
    logoBackground: "#FFFFFF",
  },
  plaid: {
    logoBackground: "#111111",
    fillColor: "#FFFFFF",
  },
  puzzle: {
    puzzleGreen: jigsawV2Colors.green500V2,
    puzzleDark: "#171629",
    puzzleLight: "#FFFFFF",
  },
  quickbooks: {
    logoBackground: "#2CA01C",
    secondaryIconFill: "#81FFBB",
    fillColor: "#FFFFFF",
  },
  ramp: {
    logoBackground: "#F7FE8B",
    logoBackgroundBrandIcons: "#E4F222",
    fillColor: "#000000",
  },
  rho: {
    logoBackground: "#151716",
  },
  relay: {
    logoBackground: "#004822",
  },
  runway: {
    logoBackground: "#F9A600",
    fillColor: "#192227",
  },
  rippling: {
    logoBackground: "#FCB315",
    fillColor: "#502D3C",
  },
  revolut: {
    logoBackground: "#FFFFFF",
    fillColor: "#5700FF",
  },
  salesforce: {
    logoBackground: "#00A1E0",
  },
  square: {
    logoBackground: "#FFFFFF",
    fillColor: "#000000",
  },
  stripe: {
    logoBackground: "#6772E5",
    logoBackgroundBrandIcons: "#635BFF",
    backgroundColor: "#FFFFFF",
  },
  stripeAtlas: {
    logoFill: "#FB0",
    logoBackgroundGradientStart: "#FFAD00",
    logoBackground: "#FF7600",
    gradientBisStart: "#FFD748",
    gradientBisMiddle: "#FFCD48",
    gradientBisEnd: "#FFCB48",
  },
  sturppy: {
    logoFill: "#FFFFFF",
    backgroundColor: "gold",
  },
  svb: {
    logoBackground: "#003149",
    logoBackgroundBrandIcons: "#FFFFFF",
    legacyBackground: "#F8F8FA",
    legacyBrandColor: "#00C0FF",
  },
  slack: {
    logoBackground: "#FFFFFF",
    brandRed: "#E01E5A",
    brandGreen: "#2EB67D",
    brandYellow: "#ECB22E",
    brandBlue: "#36C5F0",
  },
  trinet: {
    logoBackground: "#0072CE",
    logoBackgroundBrandIcons: "#0B0134",
  },
  usBank: {
    logoBackground: "#FFFFFF",
  },
  wellsFargo: {
    logoBackground: "#D71E28",
  },
  wise: {
    logoBackground: "#9EE771",
  },
};
